import React, { useMemo } from 'react';
import {
  Banner,
  Button,
  DateUtil,
  Gutter,
  Icon,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ParagraphHeader,
} from '@axiom/ui';
import type {
  Calendar,
  CalendarConversationInviteeType,
  UserInterview,
} from '@axiom/validation';

import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { InterviewGridAttendeeResponse } from '../InterviewGridAttendeeResponse/InterviewGridAttendeeResponse';
import { InterviewGridUtil } from '../../utils/interview-grid-util';

export type CancelEventSubmitProps = {
  calendarId: Calendar['id'] | null;
  eventId: CalendarConversationInviteeType['id'];
};

export type InterviewGridCancelModalProps = {
  name?: string;
  data: UserInterview;
  onClose: () => void;
  onSchedule?: (arg: UserInterview) => void;
  onSubmit: (arg: CancelEventSubmitProps) => Promise<void>;
};

export const InterviewGridCancelModal = ({
  name = 'CANCEL_INTERVIEW_MODAL',
  data,
  onClose,
  onSchedule,
  onSubmit,
}: InterviewGridCancelModalProps) => {
  const isRequest = !!data.clientRequest;
  const orderedInvitees = useMemo(
    () =>
      InterviewGridUtil.sortInviteesByPriority(
        data.calendarConversation?.invitees ?? []
      ),
    [data.calendarConversation?.invitees]
  );

  return (
    <Modal name={name}>
      <ModalHeader
        name="CANCEL_INTERVIEW_MODAL_HEADER"
        onClose={() => {
          onClose();
        }}
      >
        Cancel Interview
      </ModalHeader>
      <ModalSection>
        {isRequest ? null : (
          <>
            <Banner name="NOTIFICATION" type="critical" impact="high">
              This interview will be cancelled and all attendees will receive a
              cancellation notice.
            </Banner>
            <Gutter bottom="16px" />
          </>
        )}
        <ParagraphHeader>
          <div data-test="JOB_NAME">{data.jobName}</div>
        </ParagraphHeader>
        <Gutter bottom="16px" />
        {data.clientRequest?.calendarManualInterviewRequests[0]
          ?.availabilityNote ? (
          <RichTextEditor
            readOnly
            initialValue={
              data.clientRequest.calendarManualInterviewRequests[0]
                .availabilityNote
            }
          />
        ) : (
          <Layout direction="vertical">
            <LayoutItem bottomGutter="16px">
              <Layout direction="horizontal">
                <LayoutItem rightGutter="8px">
                  <Icon name="calendar" />
                </LayoutItem>
                <LayoutItem name="INTERVIEW_DATE">
                  {DateUtil.displayWeekDayMonthDayYear(
                    data.calendarConversation?.startTime
                  )}
                </LayoutItem>
              </Layout>
            </LayoutItem>
            <LayoutItem bottomGutter="16px">
              <Layout direction="horizontal">
                <LayoutItem rightGutter="8px">
                  <Icon name="clock" />
                </LayoutItem>
                <LayoutItem name="INTERVIEW_TIME_RANGE">
                  {DateUtil.displayTime(data.calendarConversation?.startTime)} -{' '}
                  {DateUtil.displayTime(data.calendarConversation?.endTime)}{' '}
                  {DateUtil.displayTimezoneAbbreviation(
                    data.calendarConversation?.startTime,
                    DateUtil.getCurrentTimeZone()
                  )}
                </LayoutItem>
              </Layout>
            </LayoutItem>
            <LayoutItem fluid>
              <Layout direction="horizontal">
                <LayoutItem rightGutter="8px">
                  <Icon name="avatar" />
                </LayoutItem>
                <LayoutItem fluid>
                  <Layout direction="vertical">
                    {orderedInvitees.map((invitee, idx, dataSet) => {
                      const inviteeName =
                        invitee.candidate?.calculatedDisplayName ??
                        invitee.contact?.fullName ??
                        (`${invitee.adhocAttendee?.firstName ?? ''} ${
                          invitee.adhocAttendee?.lastName ?? ''
                        }`.trim() ||
                          'person');
                      const email =
                        invitee.candidate?.email ??
                        invitee.contact?.email ??
                        invitee.adhocAttendee?.email ??
                        'email';

                      return (
                        <Gutter
                          bottom={idx < dataSet.length - 1 ? '4px' : '0'}
                          key={inviteeName + email + invitee.attendeeResponse}
                        >
                          <Layout
                            direction="horizontal"
                            position="top space-between"
                          >
                            <LayoutItem
                              name={`INVITEE_${idx}`}
                              fluid
                              className="max-lines-robust-2"
                            >
                              {inviteeName} ({email})
                            </LayoutItem>
                            <InterviewGridAttendeeResponse
                              data={invitee.attendeeResponse}
                            />
                          </Layout>
                        </Gutter>
                      );
                    })}
                  </Layout>
                </LayoutItem>
              </Layout>
            </LayoutItem>
          </Layout>
        )}
      </ModalSection>
      <ModalFooter>
        <Button
          name="CANCEL"
          pattern="primary"
          variation="outline"
          onClick={() => {
            onClose();
          }}
        >
          Keep Interview
        </Button>
        {isRequest && onSchedule ? (
          <Button
            name="SCHEDULE_NOW"
            pattern="primary"
            variation="outline"
            onClick={() => {
              onClose();
              onSchedule(data);
            }}
          >
            Schedule Now
          </Button>
        ) : null}
        <Button
          name="SUBMIT"
          pattern="primary"
          onClick={async () => {
            if (orderedInvitees[0]?.id) {
              await onSubmit({
                calendarId:
                  data.candidateOpportunity.candidate.calendar?.id ?? null,
                eventId: orderedInvitees[0].id,
              });
            }
          }}
        >
          Cancel Interview
        </Button>
      </ModalFooter>
    </Modal>
  );
};
